import React, { useState, useEffect, useMemo } from 'react';
import Axios from "axios";
import { 
    Grid, Typography, CircularProgress, Container, Box, Button, 
    Card, CardContent, CardActions, Divider, TextField,
    Paper
} from "@mui/material";
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import MapIcon from '@mui/icons-material/Map';
import PlaceIcon from '@mui/icons-material/Place';
import CollectionsBookmarkIcon from '@mui/icons-material/CollectionsBookmark';
import config, { environment } from '../../config';

function Ethnographic() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const source = Axios.CancelToken.source();
    async function fetchListings() {
      try {
        const response = await Axios.get(config[environment].geographicEndpoint + "?type=neprajzi", { cancelToken: source.token });
        const data = Array.isArray(response.data) ? response.data : [];
        setListings(data);
        setLoading(false);
      } catch (error) {
        if (!Axios.isCancel(error)) {
          console.error("Error fetching ethnographic listings:", error);
          setLoading(false);
        }
      }
    }
    fetchListings();
    return () => source.cancel();
  }, [config, environment]);

  const filteredListings = useMemo(() => {
    return listings.filter(item => {
        const matchesSearch = (item.title || '').toLowerCase().includes(searchTerm.toLowerCase()) || 
                             (item.description || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                             (item.borough || '').toLowerCase().includes(searchTerm.toLowerCase());
        return matchesSearch;
    });
  }, [listings, searchTerm]);

  if (loading) {
    return (
      <Grid container justifyContent="center" alignItems="center" sx={{ height: "100vh" }}>
        <CircularProgress />
      </Grid>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4, mt: '50px' }}>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4" sx={{ color: '#105b79', fontWeight: 'bold' }}>
          {t('SubMenu.Ethnographic')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
            <Button 
                variant="contained" 
                onClick={() => navigate('/add-sight/neprajzi')} 
                sx={{ bgcolor: '#2492be' }}
            >
              {t('SubMenu.Ethnographic')} +
            </Button>
            <Button 
                variant="contained" 
                startIcon={<MapIcon />}
                onClick={() => navigate('/terkep')}
                sx={{ bgcolor: '#105b79', '&:hover': { bgcolor: '#0d4a63' } }}
            >
                {t('Thematic.ViewOnMap', 'Megtekintés térképen')}
            </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 4, bgcolor: '#f8f9fa' }}>
        <Grid container spacing={2} alignItems="center">
            <Grid item xs={12}>
                <TextField
                    fullWidth
                    size="small"
                    label={t('Common.Search', 'Keresés')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder={t('Common.SearchPlaceholder', 'Keresés név, leírás vagy település alapján...')}
                />
            </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={3}>
        {filteredListings.length === 0 ? (
          <Typography variant="h6" sx={{ mt: 4, width: '100%', textAlign: 'center', color: 'text.secondary' }}>
            {t('Common.NoResults', 'Nincs a szűrésnek megfelelő találat.')}
          </Typography>
        ) : (
          filteredListings.map((item) => (
            <Grid item xs={12} sm={6} md={4} key={item.id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', transition: 'transform 0.2s', '&:hover': { transform: 'scale(1.02)', boxShadow: 6 } }}>
                {item.picture1 && (
                    <Box 
                        sx={{ height: 200, overflow: 'hidden', cursor: 'pointer' }}
                        onClick={() => navigate(`/terkep/${item.id}`)}
                    >
                        <img 
                            src={item.picture1} 
                            alt={item.title} 
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                        />
                    </Box>
                )}
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, cursor: 'pointer' }} onClick={() => navigate(`/terkep/${item.id}`)}>
                    <CollectionsBookmarkIcon sx={{ color: '#105b79', mr: 1 }} />
                    <Typography variant="h6" component="div" sx={{ fontWeight: 'bold', color: '#105b79' }}>
                      {item.title}
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, height: '4.5em', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' }}>
                    {item.description}
                  </Typography>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {item.borough && (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <PlaceIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                            <Typography variant="body2">
                                <strong>{t('Events.Location', 'Helyszín')}:</strong> {item.borough}
                            </Typography>
                        </Box>
                    )}
                  </Box>
                </CardContent>
                <CardActions sx={{ p: 2, pt: 0 }}>
                  <Button 
                    fullWidth 
                    variant="outlined" 
                    startIcon={<MapIcon />}
                    onClick={() => navigate(`/terkep/${item.id}`)}
                    sx={{ color: '#105b79', borderColor: '#105b79' }}
                  >
                    {t('Thematic.ViewOnMap', 'Térkép')}
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))
        )}
      </Grid>
    </Container>
  );
}

export default Ethnographic;